import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Pen } from "lucide-react";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const blank = {
  first_name: "", last_name: "", prescriber_email: "",
  professional_registration: "", registration_uf: "",
  specialty: "", birth_date: "", cpf: "",
};

export default function PrescriberProfile() {
  const [p, setP] = useState(blank);
  const [ready, setReady] = useState(false);
  const [memedId, setMemedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/prescriber/me").then((r) => {
    setP({ ...blank, ...(r.data.prescriber || {}) });
    setReady(r.data.ready);
    setMemedId(r.data.memed_prescriber_id);
    setLoading(false);
  });

  useEffect(() => { load(); }, []);

  const upd = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/prescriber/me", p);
      toast.success("Perfil de prescritor salvo");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const fmtCpf = (v) => (v || "").replace(/\D/g, "").slice(0, 11).replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4");

  if (loading) return null;

  const complete = ready;
  return (
    <Card className="card-soft" data-testid="prescriber-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-semibold text-navy-900 flex items-center gap-2"><Pen className="w-5 h-5 text-teal-600" />Perfil de prescritor (Memed)</h3>
            <p className="text-sm text-slate-600 mt-1">Dados exigidos pelo Memed para emissão de receita com assinatura digital ICP-Brasil.</p>
          </div>
          <div>
            {memedId ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-100 text-teal-800 border border-teal-200 px-3 py-1 text-xs" data-testid="memed-synced">
                <ShieldCheck className="w-3.5 h-3.5" /> Sincronizado com Memed
              </span>
            ) : complete ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-3 py-1 text-xs" data-testid="memed-pending-sync">
                <ShieldAlert className="w-3.5 h-3.5" /> Pronto — aguardando Memed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-3 py-1 text-xs" data-testid="memed-incomplete">
                <ShieldAlert className="w-3.5 h-3.5" /> Perfil incompleto
              </span>
            )}
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div><Label>Nome *</Label><Input value={p.first_name} onChange={(e)=>upd("first_name", e.target.value)} data-testid="pr-first-name" /></div>
          <div><Label>Sobrenome *</Label><Input value={p.last_name} onChange={(e)=>upd("last_name", e.target.value)} data-testid="pr-last-name" /></div>
          <div className="sm:col-span-2"><Label>Email do prescritor *</Label><Input type="email" value={p.prescriber_email} onChange={(e)=>upd("prescriber_email", e.target.value.toLowerCase())} data-testid="pr-email" /></div>
          <div><Label>Registro profissional (CRM/CRP) *</Label><Input value={p.professional_registration} onChange={(e)=>upd("professional_registration", e.target.value.replace(/\D/g, ""))} placeholder="somente números" data-testid="pr-reg" /></div>
          <div><Label>UF do registro *</Label>
            <Select value={p.registration_uf} onValueChange={(v)=>upd("registration_uf", v)}>
              <SelectTrigger data-testid="pr-uf"><SelectValue placeholder="UF" /></SelectTrigger>
              <SelectContent className="max-h-72">{UFS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Especialidade *</Label><Input value={p.specialty} onChange={(e)=>upd("specialty", e.target.value)} placeholder="Ex: Psiquiatria, Pediatria" data-testid="pr-specialty" /></div>
          <div><Label>Data de nascimento *</Label><Input type="date" value={p.birth_date} onChange={(e)=>upd("birth_date", e.target.value)} data-testid="pr-birth" /></div>
          <div><Label>CPF *</Label><Input value={fmtCpf(p.cpf)} onChange={(e)=>upd("cpf", e.target.value.replace(/\D/g, "").slice(0, 11))} placeholder="000.000.000-00" data-testid="pr-cpf" /></div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 justify-between">
          <p className="text-xs text-slate-500">Os campos marcados com * são exigidos pelo Memed. Sem eles, a receita não pode ser assinada digitalmente.</p>
          <Button onClick={save} disabled={busy} className="bg-teal-500 hover:bg-teal-600" data-testid="pr-save">{busy ? "Salvando..." : "Salvar perfil"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}
